import React, { useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import './Resources.css';
import SiteFooter from './SiteFooter';

const Resources: React.FC = () => {
  // Function to update header height CSS variable
  const updateHeaderHeight = useCallback(() => {
    const header = document.querySelector('header');
    if (header) {
      const height = header.offsetHeight;
      document.documentElement.style.setProperty('--header-height', `${height}px`);
    }
  }, []);

  useEffect(() => {
    const initialTimeout = setTimeout(() => {
      updateHeaderHeight();
    }, 100);
    
    const header = document.querySelector('header');
    if (header) {
      const resizeObserver = new ResizeObserver(() => {
        updateHeaderHeight();
      });
      
      resizeObserver.observe(header);
      window.addEventListener('resize', updateHeaderHeight);
      
      return () => {
        clearTimeout(initialTimeout);
        resizeObserver.disconnect();
        window.removeEventListener('resize', updateHeaderHeight);
      };
    } else {
      return () => {
        clearTimeout(initialTimeout);
      };
    }
  }, [updateHeaderHeight]);

  return (
    <div className="resources-page">
      <Header subtitle="Resources" showNavigation={true} />
      
      <main className="container">
        <div className="main-content">
          <div className="page-header">
            <h1>Resources</h1>
            <p className="page-subtitle">
              Essential resources for setting up and configuring Trustline protection
            </p>
          </div>

          <div className="resources-grid">
            <div className="resource-card">
              <h2>Trustline Website</h2>
              <p>
                Visit the official Trustline website to learn more about our platform, features, and services.
              </p>
              <a 
                href="https://www.trustline.id" 
                target="_blank" 
                rel="noopener noreferrer"
                className="resource-link"
              >
                Visit Trustline Website {'>'}
              </a>
            </div>

            <div className="resource-card">
              <h2>Supported Blockchains</h2>
              <p>
                Find the appropriate Trustline Validation Engine contract address for your target blockchain. 
                Our supported blockchains page provides all deployed contract versions across supported networks, 
                including mainnets and testnets.
              </p>
              <p>
                Each contract address is verified and ready to use. Simply select the version that matches 
                your blockchain and copy the address to integrate Trustline protection into your smart contracts.
              </p>
              <Link 
                to="/implementation-list" 
                className="resource-link"
              >
                View Supported Blockchains {'>'}
              </Link>
            </div>

            <div className="resource-card">
              <h2>Onboarding & Policy Configuration</h2>
              <p>
                Configure security policies for your contracts through our onboarding portal. 
                Map policies to specific contract methods and customize protection rules.
              </p>
              <div className="policy-info">
                <h3>Discovery Plan (Free)</h3>
                <p>
                  Currently, we offer an <strong>Email OTP-based Policy</strong> free of charge as part of our 
                  Discovery plan. This provides essential protection for your smart contracts.
                </p>
                <h3>Future-Proof Integration</h3>
                <p>
                  Any Trustline integration is future-proof. You can upgrade to more advanced plans that include 
                  customizable policies with additional security checks, enhanced verification methods, and 
                  advanced compliance features.
                </p>
              </div>
              <a 
                href="https://onboarding.trustline.id/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="resource-link"
              >
                Go to Onboarding Portal {'>'}
              </a>
            </div>

            <div className="resource-card">
              <h2>EVMSDK</h2>
              <p>
                The EVM SDK NPM package allows you to protect your smart contract methods with just one line of code. 
                Integrate Trustline protection seamlessly into your Solidity contracts.
              </p>
              <div className="resource-links">
                <a 
                  href="https://www.npmjs.com/package/@trustline.id/evmsdk" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="resource-link"
                >
                  View on NPM {'>'}
                </a>
                <a 
                  href="https://github.com/Trustline-id/evmsdk" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="resource-link"
                >
                  View on GitHub {'>'}
                </a>
              </div>
            </div>

            <div className="resource-card">
              <h2>WebSDK</h2>
              <p>
                The Web SDK NPM package handles user interaction, policy checks, and transaction validation seamlessly 
                in your frontend applications.
              </p>
              <div className="resource-links">
                <a 
                  href="https://www.npmjs.com/package/@trustline.id/websdk" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="resource-link"
                >
                  View on NPM {'>'}
                </a>
                <a 
                  href="https://github.com/Trustline-id/websdk" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="resource-link"
                >
                  View on GitHub {'>'}
                </a>
              </div>
            </div>

          </div>
        </div>
      </main>
      
      <SiteFooter />
    </div>
  );
};

export default Resources;

